import { Component, Input, inject } from '@angular/core';
import { ActionSheetController, Platform } from '@ionic/angular';
import { CommonUtilsService } from 'libs/common/src/lib/services/common-utils.service';
import { filter, take, switchMap } from 'rxjs/operators';
import { FileQuestionV3 } from '../../../classes/question-file';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';
import { CameraSource, CameraResultType } from '@capacitor/camera';
import chroma from 'chroma-js';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

@Component({
  selector: 'bepatient-input-file-v3',
  templateUrl: './input-file.component.html',
  styleUrls: ['./input-file.component.scss'],
  standalone: false
})
export class InputFileComponentV3 extends QuestionBaseV3ComponentDirective {
  private utils = inject(CommonUtilsService);
  private platform = inject(Platform);
  private actionSheetController = inject(ActionSheetController);
  private translate = inject(TranslateService);

  _color: string;
  contrast: string;
  @Input() question: FileQuestionV3;
  @Input()
  set color(c: string) {
    this._color = c;
    this.contrast =
      chroma(c || '#fafafa').luminance() < 0.6 ? '#fafafa' : '#2e2e2e';
  }
  get color() {
    return this._color;
  }

  get value() {
    return this.formGroup.get(this.question.key).value;
  }

  get isWebview(): boolean {
    return !(this.platform.is('android') || this.platform.is('ios'));
  }

  private fileSourceSubject = new Subject<any>();
  private fileSource$ = this.fileSourceSubject.asObservable();

  public addFile(): void {
    this.fileSource$
      .pipe(
        take(1),
        switchMap(source =>
          source == 0
            ? this.utils
                .chooseFile()
                .pipe(
                  filter(
                    result => result && result.files && result.files.length > 0
                  )
                )
            : this.utils.takePicture({
                source: source,
                resultType: CameraResultType.DataUrl
              })
        )
      )
      .subscribe({
        next: fileData => {
          // Camera capture or library image return dataUrl string with 'data:image/jpeg;base64,...'
          // Camera plugin return { dataUrl:'data:image/jpeg;base64,/9j/4AA...', format: 'jpeg', saved: false}
          if (fileData && !fileData.hasOwnProperty('files')) {
            let photoData = fileData['dataUrl'];
            if (!photoData.includes('data:image')) {
              photoData = 'data:image/jpeg;base64,' + fileData;
            }
            this.question.fileName = this.utils.generateFilename('image/jpeg');
            this.question.content = photoData;
            this.setFormControlValue(photoData);
          } else if (fileData && fileData.hasOwnProperty('files')) {
            // For file chooser we receive array of files with json object for each file selected
            // base64 string 'Axbshabsns..' is received for file instead of dataURI 'data:image/jpg;base64,Axbshabsns..'
            const nativePickerResult: any = fileData;
            let file = nativePickerResult.files[0];
            if (file && !file['data'].includes('base64')) {
              file.data = `data:${file['mimeType']};base64,${file.data}`;
            }
            this.question.content = file['data'];
            this.question.fileName = file['name'];
            this.setFormControlValue(file['data']);
          }
        },
        error: err => err
      });

    this.question.useLibrary ? this.createActionSheet() : this.setfileSource(0);
  }

  private setFormControlValue(value: any): void {
    this.formGroup.get(this.question.key).setValue(value);
    this.formGroup.get(this.question.key).markAsDirty();
    this.formGroup.get(this.question.key).markAsTouched();
  }

  /**
   * For WebView
   * File selected event to get base64 data set file content in input field
   * @param event {Event}
   */
  public fileSelected(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;
    // checking file is selected or not
    if (fileList && fileList.length > 0) {
      const fileSize = fileList[0].size / (1024 * 1024);
      this.question.fileName = fileList[0].name;
      this.createImageFromBlob(fileList[0]);
    } else {
      this.question.content = null;
      this.question.fileName = null;
      this.setFormControlValue(null);
    }
  }

  private createImageFromBlob(imageBlob: Blob): void {
    const reader = new FileReader();

    reader.onload = () => {
      const base64Image = reader.result as string;
      const base64EncodedFile = base64Image;
      this.setFormControlValue(base64EncodedFile);
      this.question.content = base64EncodedFile;
    };

    if (imageBlob) {
      reader.readAsDataURL(imageBlob);
    }
  }

  /**
   *
   * @param source {PictureSourceType} should match for camera 'PictureSourceType.CAMERA' or library 'PictureSourceType.SAVEDPHOTOALBUM'
   */
  private setfileSource(source): void {
    this.fileSourceSubject.next(source);
  }

  private createActionSheet(): void {
    this.actionSheetController
      .create({
        mode: this.platform.is('ios') ? 'ios' : 'md',
        cssClass: 'bepatient-action-sheet',
        buttons: [
          {
            text: this.translate.instant('FILE.CHOOSE_DOC'),
            icon: 'document',
            cssClass: 'bepatient-action-sheet-button',
            handler: () => {
              this.setfileSource(0);
            }
          },
          {
            text: this.translate.instant('PHOTO.CHOOSE_CAMERA'),
            icon: 'camera',
            cssClass: 'bepatient-action-sheet-button',
            handler: () => {
              this.setfileSource(CameraSource.Camera);
            }
          },
          {
            text: this.translate.instant('PHOTO.CHOOSE_PHOTO_LIBRARY'),
            icon: 'image',
            cssClass: 'bepatient-action-sheet-button',
            handler: () => {
              this.setfileSource(CameraSource.Photos);
            }
          },
          {
            text: this.translate.instant('CANCEL'),
            icon: 'close',
            cssClass: 'bepatient-action-sheet-button',
            role: 'cancel'
          }
        ]
      })
      .then(alert => alert.present());
  }
}
